/**
 * Comprehensive Quotation Creation Form
 * Saves via POST /api/invoice-requests with requestType: 'quotation'
 * Uses QT/2026-27 numbering series
 */
import { lineGross, resolveGstMode, resolvePlaceOfSupply, placeOfSupplyProblem } from '@jira-clone/shared-types';
import { PlaceOfSupplyField } from '@/components/sales/PlaceOfSupplyField';
import { useState, useMemo, useCallback, useEffect, Fragment } from 'react';
import { toast } from 'react-hot-toast';
import {
  Plus, Trash2, X, Upload,
  Loader2, Lock, CheckCircle2, FileText, BadgeDollarSign, Building2, ExternalLink
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { formatINR } from '@/shared/utils/currency';
import { useUser } from '@/shared/hooks/useAuth';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useCompanyProfile } from '@/features/recruitment/api';
import { LockedHint, MasterDataMissingBanner } from '@/components/sales/MasterDataNotice';
import { BaseRateWarningBanner } from '@/components/sales/BaseRateNotice';
import { serviceLineFields, resolveTaxMasterId } from '@/lib/serviceLines';
import { TaxSlabSelect } from '@/components/sales/TaxSlabSelect';
import { findBaseRateViolations, baseRateWarningText, formatBaseRateAmount, type BaseRateViolation } from '@/lib/baseRate';
import {
  LOCKED_INPUT_CLASS,
  describeMasterDataIssues,
  hasMasterDataIssues,
  missingBankDetails,
  missingCompanyDetails,
} from '@/lib/masterDataLock';
import { generateFullQuotationHtml } from '@/utils/sales/quotationTemplateTokens';
import { smoothScrollToTop } from '@/shared/utils/utils';
import {
  useDocumentMasterData,
  type ProductServiceData,
  type TaxMasterData,
  type TermConditionData,
  type AssumptionExclusionData,
  type PaymentTermData,
  type BankDetailData,
  type DeclarationData,
  type QuotationDefaultsData,
} from '@/shared/hooks/useDocumentMasterData';

// ── Types ─────────────────────────────────────────────────────────────────────

export const UNIT_OPTIONS = [
  'Nos',
  'Hours',
  'Weeks',
  'Month',
  'Year',
] as const;

export type UnitOption = typeof UNIT_OPTIONS[number];

interface LineItem {
  key: string;
  name: string;
  description: string;
  notes?: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  discount: number;
  discountType: 'percent' | 'fixed';
  gstRate: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  /** The Taxes / GST slab this line is billed at. Kept even when the quotation itself shows no GST, so the proforma inherits it. */
  taxMasterId?: string;
  unit: string;
  catalogItemId: string | null;
  image: string | null;
  /** The service was not found in Product & Services master data, so its rate was left at 0. */
  missingFromMaster?: boolean;
}

interface MilestoneEntry {
  key: string;
  name: string;
  description: string;
  triggerType: string;
  percentage: number;
  fixedAmount: number;
  daysFromApproval: number;
}

interface QuotationFormData {
  // Header
  quotationDate: string;
  validTillDate: string;
  poNumber: string;
  currency: string;
  preparedBy: string;

  // From (business / your details)
  businessName: string;
  businessTag: string;
  businessAddress: string;
  businessGstin: string;
  businessPan: string;
  businessPhone: string;
  businessEmail: string;
  businessWeb: string;
  businessLogo: string | null;

  // Client (Quotation For)
  clientId: string;
  sourceDealId?: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  clientGstin: string;

  // Contact Person
  contactPerson: string;
  contactDesignation: string;

  // Assumptions & Exclusions
  assumptions: string;
  assumptionsList: string[];

  // Shipping
  shippingEnabled: boolean;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingState: string;
  shippingPincode: string;

  // GST Config
  gstEnabled: boolean;
  placeOfSupply: string;
  reverseCharge: boolean;

  // Lines
  lines: LineItem[];

  // Discounts & Charges
  headerDiscount: number;
  headerDiscountType: 'percent' | 'fixed';
  additionalCharges: number;
  additionalChargesLabel: string;

  // Bank Details
  bankAccName: string;
  bankAccNo: string;
  bankName: string;
  bankBranch: string;
  bankIfsc: string;
  bankUpi: string;
  bankDetailId?: string;

  // Signatures
  signatory: string;
  signatoryRole: string;

  // Page Footer
  footerLeft: string;
  footerRight: string;

  // Footer & notes
  terms: string;
  termsList: string[];
  notes: string;
  declaration: string;
  declarationId?: string;
  signature: string | null;
  signatureUrl?: string | null;
  attachments: string[];

  // Payment Schedule
  paymentType: 'full_payment' | 'milestone_based';
  paymentTerms: string;
  advanceRequired: boolean;
  milestones: MilestoneEntry[];

  // Advanced Options
  showHsnColumn: boolean;
  showTaxSummary: boolean;
  showSkuColumn: boolean;
  showSerialNumbers: boolean;
  showThumbnails: boolean;
  showDescFullWidth: boolean;
  hideSubtotalForGroups: boolean;
  hidePlaceOfSupply: boolean;
  displayUnit: 'short' | 'full';
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const INPUT = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 dark:text-slate-100 transition-colors';
const LABEL = 'block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5';
const SECTION_CARD = 'bg-white dark:bg-slate-900 rounded-xl border border-slate-300 dark:border-slate-600 shadow-sm';

const emptyLine = (): LineItem => ({
  key: Math.random().toString(36).slice(2),
  name: '',
  description: '',
  notes: '',
  hsnSac: '',
  quantity: 1,
  rate: 0,
  discount: 0,
  discountType: 'percent',
  gstRate: 18,
  cgstRate: 9,
  sgstRate: 9,
  igstRate: 0,
  taxMasterId: '',
  unit: 'Nos',
  catalogItemId: null,
  image: null,
});

const emptyMilestone = (num: number): MilestoneEntry => ({
  key: Math.random().toString(36).slice(2),
  name: num === 1 ? 'Advance' : `Phase ${num - 1} Completion`,
  description: '',
  triggerType: num === 1 ? 'advance' : 'manual',
  percentage: 0,
  fixedAmount: 0,
  daysFromApproval: num === 1 ? 0 : num * 15,
});

const TRIGGER_TYPES = [
  { value: 'advance', label: 'Advance' },
  { value: 'date_based', label: 'Date Based' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'project_phase', label: 'Project Phase Completion' },
  { value: 'customer_approval', label: 'Customer Approval' },
  { value: 'work_completion', label: 'Work Completion' },
  { value: 'final_delivery', label: 'Final Delivery' },
  { value: 'manual', label: 'Manual Trigger' },
];

function toWords(num: number): string {
  if (num === 0) return 'zero rupees only';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  const intPart = Math.floor(Math.abs(num));
  return convert(intPart) + ' rupees only';
}

// ── Default Displayed Form Section ───────────────────────────────────────────
function Section({ title, children }: {
  title: string; children: React.ReactNode; defaultOpen?: boolean; onAdd?: () => void;
}) {
  return (
    <div className={SECTION_CARD}>
      <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

const PAYMENT_TERM_CUSTOM = '__custom__';

// ── Main Form Component ───────────────────────────────────────────────────────
interface QuotationCreateFormProps {
  onClose: () => void;
  onCreated: () => void;
  editId?: string | null;
  editData?: any;
  quotationNumber?: string;
  /** Editing a quotation that has not been emailed yet: saved in place, no new revision. */
  editInPlace?: boolean;
  /** Revising a quotation that was emailed to the client: this form creates a NEW quotation (new number) based on it. */
  revisedFrom?: { id: string; number: string } | null;
}

const DEFAULT_TERMS = `1. This quotation is valid for 30 days from the date of issue.
2. Prices are exclusive of GST; GST will be charged at the prevailing rate as shown above.
3. Payment as per the schedule above; invoices are payable within 7 days of receipt.
4. Work commences on receipt of the advance and a signed acceptance of this quotation.
5. Any scope beyond the above will be quoted separately before execution.
6. Intellectual property in the delivered work transfers to the client on receipt of full payment.
7. Either party may terminate with 15 days written notice; work completed to date remains payable.
8. Disputes are subject to the exclusive jurisdiction of the courts at Pune.`;

const DEFAULT_ASSUMPTIONS = `• Third-party licences, cloud hosting, domain, SMS/WhatsApp and payment-gateway charges are billed at actuals and are not included above.
• Content, product data, brand assets and required API credentials to be provided by the client.
• Two rounds of revisions are included per deliverable; further changes are treated as a change request.
• Timelines assume feedback and approvals within three working days at each stage.`;

export function QuotationCreateForm({
  onClose,
  onCreated,
  editId,
  editData,
  quotationNumber,
  editInPlace = false,
  revisedFrom = null,
}: QuotationCreateFormProps) {
  const user = useUser();
  const companyProfile = useCompanyProfile().data;
  const companyRegisteredAddress = useMemo(() => {
    const addr = (companyProfile as any)?.registeredAddress;
    if (!addr) return '';
    return [addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ');
  }, [companyProfile]);
  const { data: productData } = useProducts();
  const products = useMemo(() => (productData ?? []) as Array<{
    _id: string; name: string; sellingPrice?: number; hsnSac?: string;
    cgstRate?: number; sgstRate?: number; igstRate?: number; sku?: string;
  }>, [productData]);

  // Master data — single consolidated hook
  const { data: masterData, isLoading: masterLoading } = useDocumentMasterData();
  const productServices: ProductServiceData[] = masterData?.productServices ?? [];
  const taxMasters: TaxMasterData[] = masterData?.taxMasters ?? [];
  const unitMasters = masterData?.unitMasters ?? [];
  const paymentTerms: PaymentTermData[] = masterData?.paymentTerms ?? [];
  const termConditions: TermConditionData[] = masterData?.termConditions ?? [];
  const assumptionsExclusions: AssumptionExclusionData[] = masterData?.assumptionsExclusions ?? [];
  const assumptionsSectionEnabled = masterData?.quotationDefaults?.assumptionsExclusionsEnabled === true;
  const declarations: DeclarationData[] = masterData?.declarations ?? [];
  const bankDetails: BankDetailData[] = masterData?.bankDetails ?? [];
  const displaySettings = masterData?.displaySettings ?? [];
  const quotationDefaults = masterData?.quotationDefaults;

  // Derived data from master data
  const masterServicesByCategory = useMemo(() => {
    const grouped: Record<string, ProductServiceData[]> = { TECHNOLOGY: [], MARKETING_GROWTH: [], CREATIVE: [] };
    productServices.forEach((s: any) => {
      if (grouped[s?.category]) grouped[s.category].push(s);
    });
    return grouped;
  }, [productServices]);
  const allMasterServices = useMemo(() => productServices, [productServices]);
  const hsnSacCodes = masterData?.hsnSacCodes ?? [];
  const unitOptions = useMemo(() => unitMasters.map(u => u.displayName || u.shortCode || u.name), [unitMasters]);
  const defaultTerms = useMemo(() => {
    const active = (termConditions || []).filter(t => t.isActive !== false && !(t as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
    return sorted.map(t => t.content).filter(Boolean).join('\n\n');
  }, [termConditions]);

  const defaultTermsArray = useMemo(() => {
    const active = (termConditions || []).filter(t => t.isActive !== false && !(t as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return (a.displayOrder || 0) - (b.displayOrder || 0);
    });
    const lines: string[] = [];
    sorted.forEach(t => {
      const text = t.content || (t as any).description || t.title;
      if (text) {
        text.split('\n').map((s: string) => s.trim()).filter(Boolean).forEach((s: string) => lines.push(s));
      }
    });
    return lines;
  }, [termConditions]);

  const defaultAssumptions = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a =>
      a.isActive !== false && !(a as any).deletedAt && String(a.category || '').toUpperCase() === 'ASSUMPTION'
    );
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => '• ' + (a.description || (a as any).content || a.title)).filter(Boolean).join('\n');
  }, [assumptionsExclusions]);

  const defaultExclusions = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a =>
      a.isActive !== false && !(a as any).deletedAt && String(a.category || '').toUpperCase() === 'EXCLUSION'
    );
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => '• ' + (a.description || (a as any).content || a.title)).filter(Boolean).join('\n');
  }, [assumptionsExclusions]);

  const defaultAssumptionsArray = useMemo(() => {
    const active = (assumptionsExclusions || []).filter(a => a.isActive !== false && !(a as any).deletedAt);
    const sorted = [...active].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0);
    });
    return sorted.map(a => {
      const isExcl = String(a.category || '').toUpperCase() === 'EXCLUSION';
      const prefix = isExcl ? 'EXCLUSION: ' : '';
      const text = a.description || (a as any).content || a.title || '';
      return prefix + text;
    }).filter(Boolean);
  }, [assumptionsExclusions]);
  const defaultDeclaration = useMemo(() => declarations.find(d => d.isDefault)?.content || '', [declarations]);
  const defaultBankDetail = useMemo(() => bankDetails.find(b => b.isDefault && b.isActive), [bankDetails]);
  const defaultPaymentTerm = useMemo(() => paymentTerms.find(p => p.isDefault), [paymentTerms]);
  const activePaymentTerms = useMemo(() => paymentTerms.filter(p => p.isActive !== false), [paymentTerms]);
  const defaultDisplaySettings = useMemo(() => displaySettings.find(d => d.isDefault), [displaySettings]);
  const defaultQuotationDefaults = useMemo(() => quotationDefaults, [quotationDefaults]);

  const [saving, setSaving] = useState(false);
  const [availableClients, setAvailableClients] = useState<Array<{
    id: string;
    type: 'deal' | 'lead';
    name: string;
    company: string;
    contactPerson: string;
    contactDesignation: string;
    email: string;
    phone: string;
    address: string;
    gstin: string;
    state: string;
    value: number;
    description: string;
    services: string[];
  }>>([]);
  const [selectedClientServices, setSelectedClientServices] = useState<string[]>(() => {
    if (editData?.services && Array.isArray(editData.services) && editData.services.length > 0) {
      return editData.services;
    }
    if (editData?.lines && Array.isArray(editData.lines) && editData.lines.length > 0) {
      return editData.lines.map((l: any) => l.name).filter(Boolean);
    }
    return [];
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/deals?limit=100');

        const dealsList = res?.data?.deals || res?.data?.data || res?.data?.items || res?.data || [];

        const clients: typeof availableClients = [];

        if (Array.isArray(dealsList)) {
          for (const d of dealsList) {
            const id = d._id || d.id;
            // A deal gets one quotation. Keep the deal this quotation belongs to (when editing or revising it).
            if (d.quotation && ![editData?.clientId, editData?.sourceDealId, editData?.dealId].map(String).includes(String(id))) continue;
            const clientField = d.client || d.company || d.business_name || d.clientName || '';
            const title = d.title ? String(d.title).replace(/^Deal:\s*/i, '') : '';
            const company = clientField || title || 'Deal';
            const name = company;

            let contactPerson = d.contactPerson || d.contact_name || '';
            let email = d.contactEmail || d.email || '';
            let phone = d.contactPhone || d.phone || '';
            if (d.notes && typeof d.notes === 'string') {
              if (!contactPerson) {
                const cm = d.notes.match(/Contact:\s*([^,\n\)]+)/i);
                if (cm) contactPerson = cm[1].trim();
              }
              if (!email) {
                const em = d.notes.match(/Email:\s*([^,\n\)]+)/i);
                if (em) email = em[1].trim();
              }
              if (!phone) {
                const pm = d.notes.match(/Phone:\s*([^,\n\)]+)/i);
                if (pm) phone = pm[1].trim();
              }
            }

            const srvs: string[] = Array.isArray(d.services) && d.services.length > 0
              ? d.services
              : (d.service_required ? d.service_required.split(',').map((s: string) => s.trim()).filter(Boolean)
              : (d.serviceRequired ? d.serviceRequired.split(',').map((s: string) => s.trim()).filter(Boolean) : []));
            const val = Number(d.value ?? d.amount ?? 0) || 0;
            clients.push({
              id,
              type: 'deal',
              name,
              company,
              contactPerson: contactPerson || name,
              contactDesignation: d.contactDesignation || '',
              email: email || '',
              phone: phone || '',
              address: d.clientAddress || d.billingAddress || d.address || '',
              gstin: d.clientGstin || d.gstin || '',
              state: d.placeOfSupply || d.clientState || '',
              value: val,
              description: d.description || d.notes || '',
              services: srvs,
            });
          }
        }

        setAvailableClients(clients);
      } catch {
        // silent fallback
      }
    })();
  }, []);

  const handleSelectClient = (clientId: string) => {
    const c = availableClients.find(x => x.id === clientId);
    if (!c) return;

    setSelectedClientServices(c.services || []);

    let initialLines: LineItem[] = [];
    if (Array.isArray(c.services) && c.services.length > 0) {
      initialLines = c.services.map((srvName) => {
        // Rate, HSN/SAC, unit and tax come from master data; the rep enters the negotiated price on the quotation.
        const f = serviceLineFields(srvName, allMasterServices, hsnSacCodes);
        return {
          key: Math.random().toString(36).slice(2, 9),
          name: f.name,
          description: f.description,
          notes: '',
          hsnSac: f.hsnSac,
          quantity: 1,
          rate: f.rate,
          discount: 0,
          discountType: 'percent' as const,
          gstRate: f.gstRate,
          cgstRate: f.gstRate / 2,
          sgstRate: f.gstRate / 2,
          igstRate: 0,
          taxMasterId: f.taxMasterId,
          unit: f.unit,
          catalogItemId: null,
          image: null,
          missingFromMaster: f.missingFromMaster,
        };
      });
    } else if (c.value > 0) {
      initialLines = [{
        key: Math.random().toString(36).slice(2, 9),
        name: c.name || 'Commercial Services',
        description: c.description || c.name || 'Service Scope & Commercials',
        notes: '',
        hsnSac: '998314',
        quantity: 1,
        rate: c.value,
        discount: 0,
        discountType: 'percent' as const,
        gstRate: 18,
        cgstRate: 9,
        sgstRate: 9,
        igstRate: 0,
        unit: 'project',
        catalogItemId: null,
        image: null,
      }];
    }

    setForm(f => ({
      ...f,
      clientId: c.id,
      sourceDealId: c.type === 'deal' ? c.id : f.sourceDealId,
      clientName: c.company || c.name,
      contactPerson: c.contactPerson || c.name,
      contactDesignation: c.contactDesignation || f.contactDesignation,
      clientEmail: c.email || f.clientEmail,
      clientPhone: c.phone || f.clientPhone,
      clientAddress: c.address || f.clientAddress,
      clientGstin: c.gstin || f.clientGstin,
      placeOfSupply: c.state || f.placeOfSupply,
      lines: initialLines.length > 0 ? initialLines : f.lines,
    }));

    if (initialLines.length > 0) {
      toast.success(`Loaded ${initialLines.length} service(s) automatically for "${c.company || c.name}"`);
    } else {
      toast.success(`Loaded details for "${c.company || c.name}"`);
    }
  };

  useEffect(() => {
    smoothScrollToTop();
  }, []);

  useEffect(() => {
    if (companyProfile) {
      setForm((prev) => ({
        ...prev,
        businessName: prev.businessName || companyProfile.legalName || companyProfile.name || '',
        businessTag: prev.businessTag || (companyProfile as any)?.industry || '',
        businessAddress: prev.businessAddress || companyRegisteredAddress || '',
        businessGstin: companyProfile.gstin || companyProfile.gst || prev.businessGstin || '',
        businessPan: companyProfile.pan || prev.businessPan || '',
        businessPhone: prev.businessPhone || companyProfile.phone || '',
        businessEmail: prev.businessEmail || companyProfile.officialEmail || companyProfile.email || '',
        businessWeb: prev.businessWeb || companyProfile.website || '',
        businessLogo: prev.businessLogo || companyProfile.logoUrl || null,
        signatureUrl: prev.signatureUrl || companyProfile.signatureUrl || null,
      }));
    }
  }, [companyProfile, companyRegisteredAddress]);

  const buildInitialForm = useCallback((): QuotationFormData => {
    const data = editData;
    
    if (data) {
      const rawLines = data.lines || data.items || [];
      const lines: LineItem[] = rawLines.length > 0
        ? rawLines.map((l: any) => {
            const tax = Number(l.gstRate ?? (l.taxRate !== undefined ? l.taxRate : (((l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0)) || 18)));
            const cgst = l.cgstRate !== undefined ? l.cgstRate : tax / 2;
            const sgst = l.sgstRate !== undefined ? l.sgstRate : tax / 2;
            return {
              key: Math.random().toString(36).slice(2, 9),
              name: l.name || l.description || '',
              description: l.name || l.description || '',
              notes: l.notes || '',
              hsnSac: l.hsnSac || l.hsn || '',
              quantity: Number(l.quantity ?? l.qty ?? 1),
              rate: Number(l.rate ?? l.unitPrice ?? 0),
              discount: Number(l.discount ?? l.discountPercent ?? 0),
              discountType: (l.discountType || 'percent') as 'percent' | 'fixed',
              gstRate: tax,
              cgstRate: cgst,
              sgstRate: sgst,
              igstRate: Number(l.igstRate || 0),
              taxMasterId: resolveTaxMasterId(l.taxMasterId),
              unit: l.unit || 'Nos',
              catalogItemId: l.catalogItemId || l.productId || null,
              image: l.image || null,
              missingFromMaster: Boolean(l.missingFromMaster),
            };
          })
        : [emptyLine()];

      const explicitPaymentType = data.paymentType || data.paymentSchedule?.paymentType || data.paymentSchedule?.scheduleMethod || data.template_data?.paymentType;
      const isExplicitFullPayment = explicitPaymentType === 'full_payment' || explicitPaymentType === 'full';
      const isExplicitMilestone = explicitPaymentType === 'milestone_based' || explicitPaymentType === 'milestone';

      const rawMs = (isExplicitFullPayment ? [] : (data.paymentSchedule?.milestones || data.milestones || []));
      const milestones: MilestoneEntry[] = rawMs.map((m: any, idx: number) => ({
        key: Math.random().toString(36).slice(2, 9),
        name: m.name || `Phase ${idx + 1}`,
        description: m.description || '',
        triggerType: m.triggerType || 'manual',
        percentage: Number(m.percentage ?? m.percent ?? 0),
        fixedAmount: Number(m.fixedAmount || 0),
        daysFromApproval: Number(m.daysFromApproval || 0),
      }));

      const resolvedPaymentType: 'full_payment' | 'milestone_based' = isExplicitFullPayment
        ? 'full_payment'
        : (isExplicitMilestone
            ? 'milestone_based'
            : (milestones.length > 0 ? 'milestone_based' : 'full_payment'));

      const disp = data.displayOptions || {};

      return {
        quotationDate: data.date ? String(data.date).split('T')[0] : (data.quotationDate ? String(data.quotationDate).split('T')[0] : new Date().toISOString().split('T')[0]),
        validTillDate: data.validUntil ? String(data.validUntil).split('T')[0] : (data.validTillDate ? String(data.validTillDate).split('T')[0] : new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
        poNumber: data.poNumber || data.referenceNo || '',
        currency: data.currency || 'INR',
        preparedBy: data.preparedBy || data.requestedByName || user?.name || '',

        businessName: data.businessName || companyProfile?.legalName || companyProfile?.name || '',
        businessTag: data.businessTag || (companyProfile as any)?.industry || '',
        businessAddress: data.businessAddress || companyRegisteredAddress || '',
        businessGstin: data.businessGstin || companyProfile?.gstin || companyProfile?.gst || '',
        businessPan: data.businessPan || companyProfile?.pan || '',
        businessPhone: data.businessPhone || companyProfile?.phone || '',
        businessEmail: data.businessEmail || companyProfile?.officialEmail || companyProfile?.email || '',
        businessWeb: data.businessWeb || companyProfile?.website || '',
        businessLogo: data.businessLogo || companyProfile?.logoUrl || null,

        clientId: data.clientId || '',
        sourceDealId: data.sourceDealId || data.dealId || data.linkedCrmDealId || '',
        clientName: data.clientName || data.customerName || '',
        clientEmail: data.clientEmail || data.customerEmail || '',
        clientPhone: data.clientPhone || data.customerPhone || '',
        clientAddress: data.clientAddress || data.customerAddress || data.address || data.billingAddress || '',
        clientGstin: data.clientGstin || data.customerGstin || data.gstin || '',

        contactPerson: data.contactPerson || '',
        contactDesignation: data.contactDesignation || '',

        assumptions: data.assumptions || defaultAssumptions + '\n' + defaultExclusions,
        assumptionsList: data.assumptionsList || (data.assumptions ? data.assumptions.split('\n').filter((s: string) => s.trim()) : []),

        shippingEnabled: Boolean(data.shippingEnabled),
        shippingName: data.shippingName || '',
        shippingAddress: data.shippingAddress || '',
        shippingCity: data.shippingCity || '',
        shippingState: data.shippingState || '',
        shippingPincode: data.shippingPincode || '',

        gstEnabled: data.gstEnabled !== undefined ? Boolean(data.gstEnabled) : (data.gstMode ? data.gstMode !== 'none' : false),
        placeOfSupply: data.placeOfSupply || data.customerState || '',
        reverseCharge: Boolean(data.reverseCharge),

        lines,
        headerDiscount: data.headerDiscount || 0,
        headerDiscountType: data.headerDiscountType || 'fixed',
        additionalCharges: data.additionalCharges || 0,
        additionalChargesLabel: data.additionalChargesLabel || 'Shipping & Handling',

        bankAccName: data.bankAccName || defaultBankDetail?.accountName || '',
        bankAccNo: data.bankAccNo || defaultBankDetail?.accountNumber || '',
        bankName: data.bankName || defaultBankDetail?.bankName || '',
        bankBranch: data.bankBranch || defaultBankDetail?.branch || '',
        bankIfsc: data.bankIfsc || defaultBankDetail?.ifsc || '',
        bankUpi: data.bankUpi || defaultBankDetail?.upi || '',
        bankDetailId: data.bankDetailId || defaultBankDetail?.id || '',

        signatory: data.signatory || '',
        signatoryRole: data.signatoryRole || '',

        footerLeft: data.footerLeft || '',
        footerRight: data.footerRight || '',

        terms: data.terms || defaultTerms,
        termsList: data.termsList || (data.terms ? data.terms.split('\n').filter((s: string) => s.trim()) : []),
        notes: data.notes || '',
        declaration: data.declaration || defaultDeclaration || '',
        declarationId: data.declarationId || (defaultDeclaration ? declarations.find((d: any) => d.content === defaultDeclaration)?.id || '' : ''),
        signature: data.signature || null,
        signatureUrl: data.signatureUrl || data.template_data?.signatureUrl || null,
        attachments: data.attachments || [],
        paymentType: 'full_payment' as const,
        paymentTerms: data.paymentTerms || data.termsAndConditions || (paymentTerms.find(p => p.isDefault)?.name || '100% Full Payment on Delivery / Order'),
        advanceRequired: false,
        milestones: [],

        showHsnColumn: (disp.showHsnColumn !== undefined ? disp.showHsnColumn : data.showHsnColumn) !== false,
        showTaxSummary: (disp.showTaxSummary !== undefined ? disp.showTaxSummary : data.showTaxSummary) !== false,
        showSkuColumn: Boolean(disp.showSkuColumn !== undefined ? disp.showSkuColumn : data.showSkuColumn),
        showSerialNumbers: Boolean(disp.showSerialNumbers !== undefined ? disp.showSerialNumbers : data.showSerialNumbers),
        showThumbnails: Boolean(disp.showThumbnails !== undefined ? disp.showThumbnails : data.showThumbnails),
        showDescFullWidth: Boolean(disp.showDescFullWidth !== undefined ? disp.showDescFullWidth : data.showDescFullWidth),
        hideSubtotalForGroups: Boolean(disp.hideSubtotalForGroups !== undefined ? disp.hideSubtotalForGroups : data.hideSubtotalForGroups),
        hidePlaceOfSupply: Boolean(disp.hidePlaceOfSupply !== undefined ? disp.hidePlaceOfSupply : data.hidePlaceOfSupply),
        displayUnit: disp.displayUnit || data.displayUnit || (defaultDisplaySettings as any)?.displayUnit || 'short',
      };
    }

    // Build default values from master data
    const md: any = quotationDefaults;
    const defaults = {
      defaultValidityDays: md?.defaultValidityDays ?? 15,
      defaultCurrency: md?.defaultCurrency || 'INR',
      defaultGstEnabled: md?.defaultGstEnabled ?? true,
      defaultPlaceOfSupply: md?.defaultPlaceOfSupply || '',
      defaultReverseCharge: md?.defaultReverseCharge ?? false,
    } as any;

    return {
      quotationDate: new Date().toISOString().split('T')[0],
      validTillDate: new Date(Date.now() + (defaults.defaultValidityDays ?? 15) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      poNumber: '',
      currency: defaults.defaultCurrency || 'INR',
      preparedBy: user?.name || '',

      businessName: companyProfile?.legalName || companyProfile?.name || '',
      businessTag: (companyProfile as any)?.industry || '',
      businessAddress: companyRegisteredAddress || '',
      businessGstin: companyProfile?.gstin || companyProfile?.gst || '',
      businessPan: companyProfile?.pan || '',
      businessPhone: companyProfile?.phone || '',
      businessEmail: companyProfile?.officialEmail || companyProfile?.email || '',
      businessWeb: companyProfile?.website || '',
      businessLogo: companyProfile?.logoUrl || null,

      clientId: '',
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      clientAddress: '',
      clientGstin: '',

      contactPerson: '',
      contactDesignation: '',

      shippingEnabled: false,
      shippingName: '',
      shippingAddress: '',
      shippingCity: '',
      shippingState: '',
      shippingPincode: '',

      gstEnabled: false,
      // No default state: the place of supply comes from the customer's GSTIN or a state chosen on this form.
      placeOfSupply: '',
      reverseCharge: defaults?.defaultReverseCharge ?? false,

      lines: [emptyLine()],
      headerDiscount: 0,
      headerDiscountType: 'fixed',
      additionalCharges: 0,
      additionalChargesLabel: 'Shipping & Handling',

      bankAccName: defaultBankDetail?.accountName || '',
      bankAccNo: defaultBankDetail?.accountNumber || '',
      bankName: defaultBankDetail?.bankName || '',
      bankBranch: defaultBankDetail?.branch || '',
      bankIfsc: defaultBankDetail?.ifsc || '',
      bankUpi: defaultBankDetail?.upi || '',

      signatory: '',
      signatoryRole: '',

      footerLeft: '',
      footerRight: '',

      terms: defaultTerms,
      termsList: defaultTermsArray.length > 0 ? [...defaultTermsArray] : (defaultTerms ? defaultTerms.split('\n').filter((s: string) => s.trim()) : []),
      notes: '',
      assumptions: assumptionsSectionEnabled ? (defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : '')) : '',
      assumptionsList: assumptionsSectionEnabled ? (defaultAssumptionsArray.length > 0 ? [...defaultAssumptionsArray] : ((defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : '')).split('\n').filter((s: string) => s.trim()))) : [],
      declaration: defaultDeclaration || '',
      declarationId: defaultDeclaration ? declarations.find((d: any) => d.content === defaultDeclaration)?.id || '' : '',
      signature: null,
      signatureUrl: null,
      attachments: [],
      paymentType: 'full_payment' as const,
      paymentTerms: defaultPaymentTerm?.name || '100% Full Payment on Delivery / Order',
      advanceRequired: false,
      milestones: [],

      showHsnColumn: true,
      showTaxSummary: true,
      showSkuColumn: false,
      showSerialNumbers: false,
      showThumbnails: false,
      showDescFullWidth: false,
      hideSubtotalForGroups: false,
      hidePlaceOfSupply: false,
      displayUnit: 'short',
    };
  }, [
    editData,
    user?.name,
    defaultTerms,
    defaultTermsArray,
    defaultAssumptions,
    defaultExclusions,
    defaultAssumptionsArray,
    assumptionsSectionEnabled,
    defaultDeclaration,
    defaultPaymentTerm,
    defaultBankDetail,
    companyProfile,
    companyRegisteredAddress,
    declarations
  ]);

  const [form, setForm] = useState<QuotationFormData>(buildInitialForm);
  // The tax follows the place of supply: the customer's GSTIN state, else the State chosen on the form.
  const formPlace = resolvePlaceOfSupply({ customerGstin: form.clientGstin, customerState: form.placeOfSupply });
  const formPlaceLabel = formPlace.label || form.placeOfSupply || '';
  const formGstMode = resolveGstMode({
    gstEnabled: form.gstEnabled,
    reverseCharge: form.reverseCharge,
    companyGstin: form.businessGstin,
    customerGstin: form.clientGstin,
    customerState: form.placeOfSupply,
  });

  // The payment term is picked from master data; "Custom…" lets a one-off wording be typed.
  const [customPaymentTerm, setCustomPaymentTerm] = useState(false);
  const selectedPaymentTerm = useMemo(
    () => activePaymentTerms.find(t => t.name === form.paymentTerms),
    [activePaymentTerms, form.paymentTerms]
  );

  // Re-sync if editData changes
  useEffect(() => {
    if (editData) {
      setForm(buildInitialForm());
    }
  }, [editData, buildInitialForm]);

  // Sync master data defaults when masterData loads for new quotations
  useEffect(() => {
    if (!masterData || editData) return;

    setForm(prev => {
      const updates: Partial<QuotationFormData> = {};
      const hasAssumptions = prev.assumptionsList && prev.assumptionsList.some(a => a && a.trim());
      if (assumptionsSectionEnabled && !hasAssumptions && defaultAssumptionsArray.length > 0) {
        updates.assumptionsList = [...defaultAssumptionsArray];
        updates.assumptions = defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : '');
      }

      if (!prev.declaration && defaultDeclaration) {
        updates.declaration = defaultDeclaration;
        updates.declarationId = declarations.find(d => d.content === defaultDeclaration)?.id || '';
      }

      if (!prev.bankDetailId && defaultBankDetail) {
        updates.bankAccName = defaultBankDetail.accountName || prev.bankAccName;
        updates.bankAccNo = defaultBankDetail.accountNumber || prev.bankAccNo;
        updates.bankName = defaultBankDetail.bankName || prev.bankName;
        updates.bankBranch = defaultBankDetail.branch || prev.bankBranch;
        updates.bankIfsc = defaultBankDetail.ifsc || prev.bankIfsc;
        updates.bankUpi = defaultBankDetail.upi || prev.bankUpi;
        updates.bankDetailId = defaultBankDetail.id;
      }

      // A new quotation opened before master data had loaded carries the built-in fallback wording.
      if ((!prev.paymentTerms || prev.paymentTerms === '100% Full Payment on Delivery / Order') && defaultPaymentTerm) {
        updates.paymentTerms = defaultPaymentTerm.name;
      }

      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [
    masterData,
    editData,
    defaultTermsArray,
    defaultTerms,
    defaultAssumptionsArray,
    defaultAssumptions,
    defaultExclusions,
    assumptionsSectionEnabled,
    defaultDeclaration,
    defaultBankDetail,
    defaultPaymentTerm,
    declarations
  ]);

  // ── Line item helpers ─────────────────────────────────────────────────────
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const addServiceLine = (serviceName: string) => {
    const f = serviceLineFields(serviceName, allMasterServices, hsnSacCodes);
    const newLine: LineItem = {
      key: Math.random().toString(36).slice(2, 9),
      name: f.name,
      description: f.description,
      hsnSac: f.hsnSac,
      quantity: 1,
      rate: f.rate,
      discount: 0,
      discountType: 'percent' as const,
      gstRate: f.gstRate,
      cgstRate: f.gstRate / 2,
      sgstRate: f.gstRate / 2,
      igstRate: 0,
      taxMasterId: f.taxMasterId,
      unit: f.unit,
      catalogItemId: null,
      image: null,
      missingFromMaster: f.missingFromMaster,
    };
    setForm(f => {
      const hasOnlyEmpty = f.lines.length === 1 && !f.lines[0].name.trim() && f.lines[0].rate === 0;
      return {
        ...f,
        lines: hasOnlyEmpty ? [newLine] : [...f.lines, newLine]
      };
    });
    toast.success(`Added service "${f.name}" to quotation`);
  };

  const handleClientNameInput = (val: string) => {
    setForm(f => ({ ...f, clientName: val }));
    const matched = availableClients.find(
      c => (c.name && c.name.toLowerCase() === val.trim().toLowerCase()) ||
           (c.company && c.company.toLowerCase() === val.trim().toLowerCase())
    );
    if (matched && matched.id !== form.clientId) {
      handleSelectClient(matched.id);
    }
  };

  const removeLine = (key: string) => setForm(f => ({ ...f, lines: f.lines.filter(l => l.key !== key) }));
  const updateLine = (key: string, patch: Partial<LineItem>) =>
    setForm(f => ({ ...f, lines: f.lines.map(l => l.key === key ? { ...l, ...patch } : l) }));

  const pickProduct = (key: string, val: string) => {
    const p = products.find(x => x._id === val || x.name.toLowerCase() === val.toLowerCase());
    const s = allMasterServices.find(x => x.name.toLowerCase() === val.toLowerCase());

    const itemName = p?.name || s?.name || val;
    const itemRate = p?.sellingPrice !== undefined ? p.sellingPrice : (s?.defaultRate ?? 0);
    const sf = s ? serviceLineFields(s.name, allMasterServices, hsnSacCodes) : null;
    const itemHsn = p?.hsnSac || sf?.hsnSac || '';
    const itemUnit = sf?.unit || (p as any)?.billableUnit || 'Nos';
    const catId = p?._id || null;

    setForm(f => {
      const existingLine = f.lines.find(
        l => l.key !== key && ((catId && l.catalogItemId === catId) || (l.name && l.name.trim().toLowerCase() === itemName.trim().toLowerCase()))
      );

      if (existingLine) {
        const currentLine = f.lines.find(l => l.key === key);
        const qtyToAdd = currentLine?.quantity && currentLine.quantity > 0 ? currentLine.quantity : 1;
        const newQty = existingLine.quantity + qtyToAdd;

        toast.success(`Duplicate item detected: Increased "${itemName}" quantity to ${newQty}.`);

        return {
          ...f,
          lines: f.lines
            .map(l => (l.key === existingLine.key ? { ...l, quantity: newQty } : l))
            .filter(l => l.key !== key),
        };
      }

      return {
        ...f,
        lines: f.lines.map(l =>
          l.key === key
            ? {
                ...l,
                catalogItemId: catId,
                name: itemName,
                description: itemName,
                rate: itemRate,
                unit: itemUnit,
                hsnSac: itemHsn,
                cgstRate: p?.cgstRate ?? (sf ? sf.gstRate / 2 : 9),
                sgstRate: p?.sgstRate ?? (sf ? sf.gstRate / 2 : 9),
                igstRate: p?.igstRate ?? 0,
                gstRate: p ? (p.cgstRate ?? 9) + (p.sgstRate ?? 9) + (p.igstRate ?? 0) : (sf ? sf.gstRate : 18),
                taxMasterId: sf?.taxMasterId ?? '',
                missingFromMaster: false,
              }
            : l
        ),
      };
    });
  };

  const handleNameBlur = (key: string, nameValue: string) => {
    const trimmed = nameValue.trim();
    if (!trimmed) return;

    setForm(f => {
      const existingLine = f.lines.find(
        l => l.key !== key && l.name && l.name.trim().toLowerCase() === trimmed.toLowerCase()
      );

      if (existingLine) {
        const currentLine = f.lines.find(l => l.key === key);
        const qtyToAdd = currentLine?.quantity && currentLine.quantity > 0 ? currentLine.quantity : 1;
        const newQty = existingLine.quantity + qtyToAdd;

        toast.success(`Duplicate item detected: Increased "${existingLine.name}" quantity to ${newQty}.`);

        return {
          ...f,
          lines: f.lines
            .map(l => (l.key === existingLine.key ? { ...l, quantity: newQty } : l))
            .filter(l => l.key !== key),
        };
      }

      return f;
    });
  };

  // ── Calculations ──────────────────────────────────────────────────────────
  const lineCalc = useCallback((l: LineItem) => {
    const gross = lineGross(l.quantity, l.rate, l.unit);
    const discPercent = Number(l.discount) || 0;
    const disc = l.discountType === 'percent' ? gross * (discPercent / 100) : discPercent;
    const amount = Math.max(0, gross - disc);
    const gstRate = Number(l.gstRate) || 0;
    const taxAmt = form.gstEnabled ? Math.round(amount * (gstRate / 100) * 100) / 100 : 0;
    // Inside the seller's state the tax is CGST + SGST halves; to another state it is all IGST.
    const inter = formGstMode === 'inter';
    const cgst = form.gstEnabled && !inter ? Math.round(taxAmt / 2 * 100) / 100 : 0;
    const sgst = form.gstEnabled && !inter ? Math.round((taxAmt - cgst) * 100) / 100 : 0;
    const igst = form.gstEnabled && inter ? taxAmt : 0;
    const total = Math.round((amount + taxAmt) * 100) / 100;
    return { gross, disc, amount, cgst, sgst, igst, taxAmt, total };
  }, [form.gstEnabled, formGstMode]);

  const totals = useMemo(() => {
    let grossTotal = 0, totalItemDiscount = 0, subtotal = 0, taxTotal = 0;
    for (const l of form.lines) {
      const c = lineCalc(l);
      grossTotal += c.gross;
      totalItemDiscount += c.disc;
      subtotal += c.amount;
      taxTotal += c.taxAmt;
    }
    const headerDisc = form.headerDiscountType === 'percent'
      ? subtotal * (form.headerDiscount / 100)
      : form.headerDiscount;
    const grandTotal = subtotal - headerDisc + form.additionalCharges + taxTotal;
    return { grossTotal, totalItemDiscount, subtotal, headerDisc, taxTotal, grandTotal };
  }, [form.lines, form.headerDiscount, form.headerDiscountType, form.additionalCharges, form.gstEnabled, lineCalc]);

  // Base Rate guard — every line is checked independently against its master-data
  // service's minimum selling rate after discount. Recomputed live so the warning
  // follows changes to the service, rate, quantity and discount.
  const baseRateViolations = useMemo(() => {
    return findBaseRateViolations(form.lines, allMasterServices);
  }, [form.lines, allMasterServices]);
  const violationsByLineKey = useMemo(() => {
    const map = new Map<string, BaseRateViolation<LineItem>>();
    for (const v of baseRateViolations) map.set(v.line.key, v);
    return map;
  }, [baseRateViolations]);

  // ── Generate HTML Template ─────────────────────────────────────────────────
  const generateHtmlBody = (items: typeof form.lines, docNumber?: string) => {
    const today = new Date(form.quotationDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const validTill = form.validTillDate ? new Date(form.validTillDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    return generateFullQuotationHtml({
      docType: 'quotation',
      quotationNumber: docNumber || 'QT/2026-27/001',
      quotationDate: today,
      validUntil: validTill,
      paymentTerms: form.paymentTerms || '100% Full Payment on Delivery / Order',
      paymentTermsDescription: selectedPaymentTerm?.description || '',
      paymentType: 'full_payment',
      currency: form.currency,
      gstMode: formGstMode,
      gstEnabled: form.gstEnabled,
      reverseCharge: form.reverseCharge ? 'Yes' : 'No',
      showHsn: form.showHsnColumn,
      displayOptions: {
        showHsnColumn: form.showHsnColumn,
        showTaxSummary: form.showTaxSummary,
        showSkuColumn: form.showSkuColumn,
        showSerialNumbers: form.showSerialNumbers,
        showThumbnails: form.showThumbnails,
        showDescFullWidth: form.showDescFullWidth,
        hideSubtotalForGroups: form.hideSubtotalForGroups,
        hidePlaceOfSupply: form.hidePlaceOfSupply,
        displayUnit: form.displayUnit,
      },
      headerDiscount: form.headerDiscount,
      headerDiscountType: form.headerDiscountType,
      additionalCharges: form.additionalCharges,
      additionalChargesLabel: form.additionalChargesLabel,
      subtotal: totals.subtotal,
      taxTotal: form.gstEnabled ? totals.taxTotal : 0,
      grandTotal: totals.grandTotal,
      total: totals.grandTotal,

      companyName: form.businessName,
      companyTag: form.businessTag,
      companyAddress: form.businessAddress,
      companyGstin: form.businessGstin,
      companyPan: form.businessPan,
      companyState: (companyProfile as any)?.registeredAddress?.state || '',
      companyPhone: form.businessPhone,
      companyEmail: form.businessEmail,
      companyWeb: form.businessWeb,
      companyLogo: form.businessLogo || undefined,

      customerName: form.clientName,
      customerAddress: form.clientAddress,
      customerEmail: form.clientEmail,
      customerPhone: form.clientPhone,
      customerGstin: form.clientGstin,
      customerState: formPlaceLabel,

      contactPerson: form.contactPerson,
      contactDesignation: form.contactDesignation,
      preparedBy: form.preparedBy,
      assumptions: assumptionsSectionEnabled
        ? ((form.assumptionsList && form.assumptionsList.filter(s => s && s.trim()).length > 0)
          ? form.assumptionsList.filter(s => s && s.trim())
          : (form.assumptions ? form.assumptions.split('\n').filter(Boolean) : undefined))
        : undefined,
      assumptionsList: assumptionsSectionEnabled && (form.assumptionsList && form.assumptionsList.filter(s => s && s.trim()).length > 0)
        ? form.assumptionsList.filter(s => s && s.trim())
        : undefined,
      assumptionsEnabled: assumptionsSectionEnabled,
      terms: (form.termsList && form.termsList.filter(s => s && s.trim()).length > 0)
        ? form.termsList.filter(s => s && s.trim())
        : (form.terms ? form.terms.split('\n').filter(Boolean) : undefined),
      termsList: (form.termsList && form.termsList.filter(s => s && s.trim()).length > 0)
        ? form.termsList.filter(s => s && s.trim())
        : undefined,
      notes: form.notes ? form.notes.split('\n').filter(Boolean) : undefined,

      bankAccName: form.bankAccName,
      bankAccNo: form.bankAccNo,
      bankName: form.bankName,
      bankBranch: form.bankBranch,
      bankIfsc: form.bankIfsc,
      bankUpi: form.bankUpi,

      declaration: form.declaration,
      signatory: form.signatory,
      signatoryRole: form.signatoryRole,
      signatureUrl: companyProfile?.signatureUrl || (typeof window !== 'undefined' ? localStorage.getItem('dt_company_signature') : '') || undefined,

      footerLeft: form.footerLeft,
      footerRight: form.footerRight,

      reference: form.poNumber,
      items: items.map(l => {
        const c = lineCalc(l);
        const subParts: string[] = [];
        if (l.notes && l.notes.trim()) {
          subParts.push(l.notes.trim());
        }
        const subDescription = subParts.length > 0 ? subParts.join('\n') : undefined;

        return {
          name: l.name,
          description: l.description || l.name,
          notes: l.notes || '',
          subDescription,
          catalogItemId: l.catalogItemId || undefined,
          image: l.image || undefined,
          hsn: l.hsnSac,
          hsnSac: l.hsnSac,
          qty: l.quantity,
          quantity: l.quantity,
          unit: l.unit || 'Nos',
          rate: l.rate,
          unitPrice: l.rate,
          disc: l.discountType === 'percent' ? l.discount : (l.rate > 0 && l.quantity > 0 ? (l.discount / lineGross(l.quantity, l.rate, l.unit)) * 100 : 0),
          discount: l.discount,
          discountPercent: l.discountType === 'percent' ? l.discount : 0,
          // The line's tax type is always recorded; only the tax amount follows the GST switch,
          // so a no-GST quotation still hands its slab to the proforma. `taxRate` is the field
          // the API keeps — `gstRate` alone would be dropped and the slab would arrive rate-less.
          gst: Number(l.gstRate) || 0,
          gstRate: Number(l.gstRate) || 0,
          taxRate: Number(l.gstRate) || 0,
          taxMasterId: l.taxMasterId || null,
          taxableValue: c.amount,
          taxAmount: form.gstEnabled ? c.taxAmt : 0,
          lineTotal: c.total,
          total: c.total,
        };
      }),
      milestones: form.paymentType === 'milestone_based' ? form.milestones.map((m, idx) => {
        const pct = m.percentage || 0;
        const netAmt = pct > 0 ? Math.round((totals.subtotal * pct / 100) * 100) / 100 : (m.fixedAmount || 0);
        const grossAmt = netAmt;
        return {
          milestoneNumber: idx + 1,
          name: m.name,
          percent: pct,
          percentage: pct,
          due: m.daysFromApproval ? `T + ${m.daysFromApproval} days` : 'On milestone completion',
          netAmount: netAmt,
          taxAmount: 0,
          cgstAmount: 0,
          sgstAmount: 0,
          amountDue: grossAmt,
          amount: grossAmt,
          cgstRate: 0,
          sgstRate: 0,
          gstRate: 0,
        };
      }) : [],
      availableProducts: products || [],
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  // Company and bank details come from master data and are read-only here; a document can't be saved without them.
  const masterDataIssues = {
    company: missingCompanyDetails({ name: form.businessName, address: form.businessAddress, gstin: form.businessGstin }),
    bank: missingBankDetails({ accountName: form.bankAccName, accountNumber: form.bankAccNo, bankName: form.bankName, ifsc: form.bankIfsc }),
  };
  const masterDataBlocked = hasMasterDataIssues(masterDataIssues);

  const handleSubmit = async (asDraft: boolean) => {
    if (masterDataBlocked) { toast.error(describeMasterDataIssues(masterDataIssues)); return; }
    if (!form.clientName.trim()) { toast.error('Client name is required.'); return; }
    const validLines = form.lines.filter(l => l.name.trim() && l.quantity > 0);
    if (validLines.length === 0) { toast.error('Add at least one line item.'); return; }
    if (!asDraft) {
      const placeIssue = placeOfSupplyProblem({
        gstEnabled: form.gstEnabled,
        reverseCharge: form.reverseCharge,
        customerGstin: form.clientGstin,
        customerState: form.placeOfSupply,
      });
      if (placeIssue) { toast.error(placeIssue); return; }
      const belowBaseRate = findBaseRateViolations(validLines, allMasterServices);
      if (belowBaseRate.length > 0) {
        const names = Array.from(new Set(belowBaseRate.map(v => v.name))).join(', ');
        toast.error(`Quotation cannot be submitted because one or more services are below their minimum Base Rate: ${names}.`);
        return;
      }
    }

    setSaving(true);
    try {
      const items = validLines.map(l => {
        const c = lineCalc(l);
        return {
          catalogItemId: l.catalogItemId || null,
          name: l.name,
          description: l.name,
          notes: l.notes || '',
          hsnSac: l.hsnSac,
          hsn: l.hsnSac,
          unit: l.unit || 'Nos',
          quantity: l.quantity,
          qty: l.quantity,
          rate: l.rate,
          unitPrice: l.rate,
          discount: l.discount,
          discountPercent: l.discountType === 'percent' ? l.discount : 0,
          disc: l.discountType === 'percent' ? l.discount : 0,
          taxRate: l.gstRate,
          gstRate: l.gstRate,
          taxMasterId: l.taxMasterId || null,
          cgstRate: l.cgstRate,
          sgstRate: l.sgstRate,
          igstRate: l.igstRate,
          taxableValue: c.amount,
          taxAmount: c.taxAmt,
          lineTotal: c.total,
          total: c.total,
        };
      });

      // Generate the HTML template for the quotation document view
      // The server writes the issued number into the saved document for a new quotation; when editing
      // one that exists, its own number is used here so the document is already right.
      const htmlBody = generateHtmlBody(validLines, editId && !revisedFrom ? (editData?.number || editData?.documentNumber || quotationNumber) : undefined);

      const scheduleData = null;

      const docPayload = {
        clientName: form.clientName.trim(),
        clientId: form.clientId || undefined,
        sourceDealId: form.sourceDealId || (editData && (editData.sourceDealId || editData.dealId)) || undefined,
        clientEmail: (form.clientEmail && form.clientEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.clientEmail.trim())) ? form.clientEmail.trim() : '',
        clientPhone: form.clientPhone ? form.clientPhone.trim() : '',
        clientAddress: form.clientAddress || '',
        clientGstin: form.clientGstin || '',
        clientState: formPlaceLabel,

        contactPerson: form.contactPerson || '',
        contactDesignation: form.contactDesignation || '',

        businessName: form.businessName,
        businessTag: form.businessTag,
        businessAddress: form.businessAddress,
        businessGstin: form.businessGstin,
        businessPan: form.businessPan,
        businessPhone: form.businessPhone,
        businessEmail: form.businessEmail,
        businessWeb: form.businessWeb,
        businessLogo: form.businessLogo || undefined,
        preparedBy: form.preparedBy,

        terms: form.termsList && form.termsList.length > 0 ? form.termsList.filter(s => s && s.trim()).join('\n') : form.terms,
        termsList: form.termsList && form.termsList.length > 0 ? form.termsList.filter(s => s && s.trim()) : [],
        assumptions: assumptionsSectionEnabled ? (form.assumptionsList && form.assumptionsList.length > 0 ? form.assumptionsList.filter(s => s && s.trim()).join('\n') : form.assumptions) : '',
        assumptionsList: assumptionsSectionEnabled && form.assumptionsList && form.assumptionsList.length > 0 ? form.assumptionsList.filter(s => s && s.trim()) : [],
        declaration: form.declaration,
        declarationId: form.declarationId,

        bankAccName: form.bankAccName,
        bankAccNo: form.bankAccNo,
        bankName: form.bankName,
        bankBranch: form.bankBranch,
        bankIfsc: form.bankIfsc,
        bankUpi: form.bankUpi,
        bankDetailId: form.bankDetailId,

        signatory: form.signatory,
        signatoryRole: form.signatoryRole,

        footerLeft: form.footerLeft,
        footerRight: form.footerRight,

        gstEnabled: form.gstEnabled,
        reverseCharge: form.reverseCharge,
        placeOfSupply: form.placeOfSupply,

        shippingEnabled: form.shippingEnabled,
        shippingName: form.shippingName,
        shippingAddress: form.shippingAddress,
        shippingCity: form.shippingCity,
        shippingState: form.shippingState,
        shippingPincode: form.shippingPincode,

        headerDiscount: form.headerDiscount,
        headerDiscountType: form.headerDiscountType,
        additionalCharges: form.additionalCharges,
        additionalChargesLabel: form.additionalChargesLabel,

        displayOptions: {
          showHsnColumn: form.showHsnColumn,
          showTaxSummary: form.showTaxSummary,
          showSkuColumn: form.showSkuColumn,
          showSerialNumbers: form.showSerialNumbers,
          showThumbnails: form.showThumbnails,
          showDescFullWidth: form.showDescFullWidth,
          hideSubtotalForGroups: form.hideSubtotalForGroups,
          hidePlaceOfSupply: form.hidePlaceOfSupply,
          displayUnit: form.displayUnit,
        },

        date: form.quotationDate,
        validUntil: form.validTillDate,
        referenceNo: form.poNumber,
        items,
        currency: form.currency,
        notes: form.notes,
        paymentType: 'full_payment',
        advanceRequired: false,
        paymentTerms: form.paymentTerms || '100% Full Payment on Delivery / Order',
        subtotal: totals.subtotal,
        taxTotal: form.gstEnabled ? totals.taxTotal : 0,
        total: totals.grandTotal,
        grandTotal: totals.grandTotal,
        grandTotalMinor: Math.round(totals.grandTotal * 100),
        gstMode: formGstMode,
        htmlBody,
        currentHtmlBody: htmlBody,
        paymentSchedule: null,
        milestones: [],
        template_data: {
          businessName: form.businessName,
          businessTag: form.businessTag,
          businessAddress: form.businessAddress,
          businessGstin: form.businessGstin,
          businessPan: form.businessPan,
          businessPhone: form.businessPhone,
          businessEmail: form.businessEmail,
          businessWeb: form.businessWeb,
          businessLogo: form.businessLogo || undefined,
          companyLogo: form.businessLogo || undefined,
          clientName: form.clientName,
          clientEmail: form.clientEmail,
          clientPhone: form.clientPhone,
          clientAddress: form.clientAddress,
          clientGstin: form.clientGstin,
          contactPerson: form.contactPerson,
          contactDesignation: form.contactDesignation,
          terms: form.terms,
          termsList: form.termsList,
          assumptions: assumptionsSectionEnabled ? form.assumptions : '',
          assumptionsList: assumptionsSectionEnabled ? form.assumptionsList : [],
          notes: form.notes,
          declaration: form.declaration,
          declarationId: form.declarationId,
          bankAccName: form.bankAccName,
          bankAccNo: form.bankAccNo,
          bankName: form.bankName,
          bankBranch: form.bankBranch,
          bankIfsc: form.bankIfsc,
          bankUpi: form.bankUpi,
          bankDetailId: form.bankDetailId,
          signatory: form.signatory,
          signatoryRole: form.signatoryRole,
          signatureUrl: form.signatureUrl || companyProfile?.signatureUrl || undefined,
          footerLeft: form.footerLeft,
          footerRight: form.footerRight,
          paymentType: form.paymentType,
          milestones: form.paymentType === 'milestone_based' && scheduleData ? (scheduleData as any).milestones : [],
          items,
          gstEnabled: form.gstEnabled,
          gstMode: formGstMode,
          subtotal: totals.subtotal,
          taxTotal: form.gstEnabled ? totals.taxTotal : 0,
          total: totals.grandTotal,
          grandTotal: totals.grandTotal,
        },
        ...(revisedFrom ? { revisedFromId: revisedFrom.id } : {}),
      };

      if (editId) {
        // Update the existing quotation (an invoice request)
        await api.put(`/invoice-requests/${editId}`, docPayload, { skipErrorToast: true });

        if (!asDraft) {
          // A draft becomes an active quotation. No approval is needed.
          await api.post(`/invoice-requests/${editId}/submit`, undefined, { skipErrorToast: true });
          toast.success('Quotation confirmed.');
        } else {
          toast.success(editInPlace ? 'Quotation updated.' : 'Quotation draft updated.');
        }
      } else {
        // Create new quotation. A quotation is active the moment it is created.
        await api.post('/invoice-requests', {
          ...docPayload,
          requestType: 'quotation',
          status: asDraft ? 'draft' : 'approved',
        }, { skipErrorToast: true });

        toast.success(asDraft ? 'Quotation saved as draft.' : revisedFrom ? 'Revised quotation created.' : 'Quotation created.');
      }

      onCreated();
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'save this quotation'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {editInPlace
              ? 'Edit Quotation'
              : revisedFrom
                ? 'Revise Quotation'
                : editId
                  ? 'Edit Draft Quotation'
                  : 'Create Quotation'}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {editInPlace
              ? `Quotation ${quotationNumber || editData?.number || editData?.documentNumber || ''} has not been emailed yet, so changes are saved on the same quotation. Once it is emailed, revise it to create a new one.`
              : revisedFrom
                ? `This creates a new quotation based on ${revisedFrom.number}. The original stays as it was.`
                : editId
                  ? 'Update the draft, or create the quotation to make it active.'
                  : 'Fill in details to generate a quotation (QT/2026-27 series)'}
          </p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* ── Header Section ─────────────────────────────────────────────────── */}
      <div className={SECTION_CARD + ' p-5'}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className={LABEL}>Quotation Date *</label>
            <input type="date" className={INPUT} value={form.quotationDate}
              onChange={e => setForm(f => ({ ...f, quotationDate: e.target.value }))} required />
          </div>
          <div>
            <label className={LABEL}>Valid Till Date</label>
            <input type="date" className={INPUT} value={form.validTillDate}
              onChange={e => setForm(f => ({ ...f, validTillDate: e.target.value }))} />
          </div>
          <div>
            <label className={LABEL}>Prepared By</label>
            <input className={INPUT} placeholder="Salesperson name" value={form.preparedBy}
              onChange={e => setForm(f => ({ ...f, preparedBy: e.target.value }))} />
          </div>
          <div>
            <label className={LABEL}>PO / Ref Number</label>
            <input className={INPUT} placeholder="Client PO or RFQ ref" value={form.poNumber}
              onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} />
          </div>
          <div>
            <label className={LABEL}>Currency *</label>
            <select className={INPUT} value={form.currency}
              onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
              <option value="INR">INR - Indian Rupee (₹)</option>
              <option value="USD">USD - US Dollar ($)</option>
              <option value="EUR">EUR - Euro (€)</option>
              <option value="GBP">GBP - British Pound (£)</option>
            </select>
          </div>
        </div>
      </div>

      <MasterDataMissingBanner company={masterDataIssues.company} bank={masterDataIssues.bank} />

      {/* ── From & To ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Quotation From */}
        <div className={SECTION_CARD + ' p-5'}>
          <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Quotation From (Your Details)</h3>
          <div className="mb-3"><LockedHint source="company" /></div>
          <div className="space-y-3">
            {/* Company Logo (Disabled) */}
            <div className="opacity-60 pointer-events-none select-none relative">
              <div className="flex items-center justify-between mb-1">
                <label className={LABEL + " !mb-0"}>Company Logo</label>
                <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700">Disabled</span>
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="w-24 h-14 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg flex items-center justify-center cursor-not-allowed bg-slate-50 dark:bg-slate-800/50 overflow-hidden"
                  title="Company Logo upload is disabled"
                >
                  {form.businessLogo ? (
                    <img src={form.businessLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <div className="text-center">
                      <Upload className="w-3.5 h-3.5 text-slate-400 mx-auto" />
                      <span className="text-[9px] text-slate-400 font-semibold block mt-0.5">Upload Logo</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="formLogoUpload"
                  accept="image/*"
                  disabled
                  className="hidden"
                  onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      const r = new FileReader();
                      r.onload = (ev) => setForm(f => ({ ...f, businessLogo: ev.target?.result as string }));
                      r.readAsDataURL(e.target.files[0]);
                    }
                  }}
                />
                <div className="text-[11px] text-slate-400">
                  <span className="font-semibold text-slate-700 dark:text-slate-300 block">Header Logo</span>
                  Reflected on Quotations, Proforma & Tax Invoices
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Company / Brand Name</label>
                <input className={INPUT + ' ' + LOCKED_INPUT_CLASS} value={form.businessName} readOnly aria-readonly="true" />
              </div>
              <div>
                <label className={LABEL}>Tagline</label>
                <input className={INPUT + ' ' + LOCKED_INPUT_CLASS} value={form.businessTag} readOnly aria-readonly="true" />
              </div>
            </div>
            <div>
              <label className={LABEL}>Registered Office Address</label>
              <input className={INPUT + ' ' + LOCKED_INPUT_CLASS} value={form.businessAddress} readOnly aria-readonly="true" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Company GSTIN</label>
                <input className={INPUT + ' ' + LOCKED_INPUT_CLASS} value={form.businessGstin} readOnly aria-readonly="true" />
              </div>
              <div>
                <label className={LABEL}>Company PAN</label>
                <input className={INPUT + ' ' + LOCKED_INPUT_CLASS} value={form.businessPan} readOnly aria-readonly="true" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className={LABEL}>Phone</label>
                <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.businessPhone} readOnly aria-readonly="true" />
              </div>
              <div>
                <label className={LABEL}>Email</label>
                <input type="email" className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.businessEmail} readOnly aria-readonly="true" />
              </div>
              <div>
                <label className={LABEL}>Website</label>
                <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.businessWeb} readOnly aria-readonly="true" />
              </div>
            </div>
          </div>
        </div>

        {/* Quotation For */}
        <div className={SECTION_CARD + ' p-5'}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Quotation For (Client Details)</h3>
            {availableClients.length > 0 && (
              <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
                Auto-fill Name, Contact & Services from CRM
              </span>
            )}
          </div>
          <div className="space-y-3">
            {form.sourceDealId && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 rounded-xl bg-gradient-to-r from-emerald-500/15 via-emerald-500/10 to-teal-500/15 border border-emerald-500/30 text-xs text-emerald-900 dark:text-emerald-200 shadow-2xs">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-emerald-600 text-white shadow-2xs shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-foreground">
                        Linked CRM Deal
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Quotation dynamically linked to deal: <span className="font-semibold text-foreground">{form.clientName}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="px-2 py-1 rounded-md bg-emerald-100 dark:bg-emerald-950 border border-emerald-300 dark:border-emerald-800 text-[11px] font-mono font-semibold text-emerald-800 dark:text-emerald-300">
                    Deal ID: {form.sourceDealId}
                  </span>
                </div>
              </div>
            )}
            {availableClients.length > 0 && (
              <div>
                <label className={LABEL}>Select Client from CRM (Deals)</label>
                <select
                  className={INPUT + ' text-xs text-slate-800 dark:text-slate-100 font-semibold bg-slate-50/80 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600'}
                  value={form.clientId || ''}
                  onChange={e => handleSelectClient(e.target.value)}
                >
                  <option value="">-- Choose Deal ({availableClients.length} available) --</option>
                  <optgroup label="Deals (Active Commercial Proposals)">
                    {availableClients.filter(c => c.type === 'deal').map(c => (
                      <option key={`deal-${c.id}`} value={c.id}>
                        {c.company || c.name} {c.services.length ? `• Services: ${c.services.join(', ')}` : ''} {c.value ? `(₹${c.value.toLocaleString('en-IN')})` : ''}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={LABEL + ' mb-0'}>Client / Company Name *</label>
                {availableClients.length > 0 && (
                  <span className="text-[11px] text-slate-500">
                    Type to search or select from CRM list
                  </span>
                )}
              </div>
              <input
                className={INPUT}
                placeholder="Client / Business name"
                list="crm-clients-datalist"
                value={form.clientName}
                onChange={e => handleClientNameInput(e.target.value)}
                required
              />
              <datalist id="crm-clients-datalist">
                {availableClients.map(c => (
                  <option key={c.id} value={c.company || c.name}>
                    {c.services.length ? `Services: ${c.services.join(', ')}` : c.type.toUpperCase()}
                  </option>
                ))}
              </datalist>
            </div>

            {selectedClientServices.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-300">
                <span className="font-semibold flex items-center gap-1">
                  ✨ Auto-filled Services:
                </span>
                {selectedClientServices.map((srv, idx) => (
                  <span key={idx} className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/60 font-medium">
                    {srv}
                  </span>
                ))}
                <span className="text-[11px] text-emerald-600 dark:text-emerald-400 ml-1">
                  (Added to quotation table below)
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Contact Person</label>
                <input className={INPUT} placeholder="Contact Person Name" value={form.contactPerson}
                  onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} />
              </div>
              <div>
                <label className={LABEL}>Designation</label>
                <input className={INPUT} placeholder="e.g. CTO, Procurement" value={form.contactDesignation}
                  onChange={e => setForm(f => ({ ...f, contactDesignation: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Client Email</label>
                <input type="email" className={INPUT} placeholder="client@email.com" value={form.clientEmail}
                  onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))} />
              </div>
              <div>
                <label className={LABEL}>Client Phone</label>
                <input className={INPUT} placeholder="+91 XXXXX XXXXX" value={form.clientPhone}
                  onChange={e => setForm(f => ({ ...f, clientPhone: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={LABEL}>Client GSTIN</label>
              <input className={INPUT} placeholder="22AAAAA0000A1Z5" value={form.clientGstin}
                onChange={e => setForm(f => ({ ...f, clientGstin: e.target.value.toUpperCase() }))} />
            </div>
            <div>
              <label className={LABEL}>Client Billing Address</label>
              <textarea className={INPUT + ' resize-none'} rows={2} value={form.clientAddress}
                onChange={e => setForm(f => ({ ...f, clientAddress: e.target.value }))} />
            </div>
          </div>
        </div>
      </div>

      {/* ── GST Configuration ──────────────────────────────────────────────── */}
      <Section title="GST Configuration">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Configure GST applicability for this quotation. When disabled, tax is not calculated or included in totals.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.gstEnabled}
              onChange={e => setForm(f => ({ ...f, gstEnabled: e.target.checked }))}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
            />
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Enable GST</span>
          </label>
          <div>
            <PlaceOfSupplyField
              customerGstin={form.clientGstin}
              state={form.placeOfSupply}
              sellerGstin={form.businessGstin}
              gstApplies={form.gstEnabled && !form.reverseCharge}
              onChange={(placeOfSupply) => setForm(f => ({ ...f, placeOfSupply }))}
              inputClassName={INPUT}
              labelClassName={LABEL}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.reverseCharge}
              onChange={e => setForm(f => ({ ...f, reverseCharge: e.target.checked }))}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
            />
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Reverse Charge Applicable</span>
          </label>
        </div>
      </Section>

      {/* ── Line Items ─────────────────────────────────────────────────────── */}
      <div className={SECTION_CARD}>
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Line Items & Services</h3>
              <p className="text-[11px] text-slate-400">Services from client are loaded automatically below, or click any service to add.</p>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
              {form.lines.length} item(s)
            </span>
          </div>
          {/* Quick add service pills */}
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-[11px] font-semibold text-slate-500 mr-1">+ Quick Add Service:</span>
            {allMasterServices.slice(0, 7).map((srv) => (
              <button
                key={srv.name}
                type="button"
                onClick={() => addServiceLine(srv.name)}
                className="text-[11px] px-2.5 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800 bg-indigo-50/70 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 transition-colors cursor-pointer font-medium"
              >
                + {srv.name}
              </button>
            ))}
          </div>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1200px]">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5 text-left min-w-[260px]">Description of goods / services</th>
                {form.showHsnColumn && <th className="px-2 py-2.5 text-center w-28">HSN/SAC</th>}
                <th className="px-2 py-2.5 text-center w-24">Qty</th>
                <th className="px-2 py-2.5 text-center w-32">Unit</th>
                <th className="px-2 py-2.5 text-right w-32">Rate (₹)</th>
                <th className="px-2 py-2.5 text-right w-28">Disc%</th>
                <th className="px-2 py-2.5 text-right w-32">Taxable (₹)</th>
                <th className="px-2 py-2.5 text-center w-36">Tax type</th>
                {form.gstEnabled && <th className="px-2 py-2.5 text-center w-24">GST %</th>}
                <th className="px-2 py-2.5 text-right w-32">Total (₹)</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {form.lines.map((line) => {
                const calc = lineCalc(line);
                const baseRateViolation = violationsByLineKey.get(line.key);
                return (
                  <Fragment key={line.key}>
                  <tr className={baseRateViolation
                    ? 'bg-amber-50/70 dark:bg-amber-950/20 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                    : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/20'}
                  >
                    {/* Item name / product picker */}
                    <td className="px-3 py-2">
                      {(() => {
                        const lineNameTrimmed = (line.name || '').trim();
                        const matchedMasterService = allMasterServices.find(
                          s => s.name.toLowerCase() === lineNameTrimmed.toLowerCase()
                        );
                        // Only match product by catalogItemId, NOT by name (to avoid collision with master services)
                        const matchedProduct = line.catalogItemId
                          ? products.find(p => p._id === line.catalogItemId)
                          : undefined;
                        // selectedValue: prefer matched product ID, then master service name,
                        // then the raw line name. Never use a bare catalogItemId that has no
                        // matching <option> (i.e. matchedProduct is undefined but catalogItemId set).
                        const selectedValue = matchedProduct?._id
                          || matchedMasterService?.name
                          || lineNameTrimmed
                          || '';

                        return (
                          <div className="space-y-1">
                            <select
                              className={INPUT + ' text-xs min-w-[240px] font-medium'}
                              value={selectedValue}
                              onChange={e => {
                                const val = e.target.value;
                                if (val) {
                                  pickProduct(line.key, val);
                                } else {
                                  updateLine(line.key, { catalogItemId: null, name: '', description: '' });
                                }
                              }}
                            >
                              <option value="">— Select item / service —</option>
                              {/* Pinned current selection — always shown at top when name is filled */}
                              {lineNameTrimmed && (
                                <option value={selectedValue}>✓ {lineNameTrimmed}</option>
                              )}
                              <optgroup label="Technology Services">
                                {masterServicesByCategory.TECHNOLOGY?.map(s => (
                                  <option key={s.name} value={s.name}>
                                    {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Marketing & Growth Services">
                                {masterServicesByCategory.MARKETING_GROWTH?.map(s => (
                                  <option key={s.name} value={s.name}>
                                    {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="Creative Services">
                                {masterServicesByCategory.CREATIVE?.map(s => (
                                  <option key={s.name} value={s.name}>
                                    {s.name} (₹{(s.defaultRate || 0).toLocaleString('en-IN')})
                                  </option>
                                ))}
                              </optgroup>
                              {products.filter(p => !allMasterServices.some(s => s.name.toLowerCase() === p.name.toLowerCase())).length > 0 && (
                                <optgroup label="Other Catalog Items">
                                  {products
                                    .filter(p => !allMasterServices.some(s => s.name.toLowerCase() === p.name.toLowerCase()))
                                    .map(p => (
                                      <option key={p._id} value={p._id}>
                                        {p.name} {p.sellingPrice !== undefined ? `(₹${p.sellingPrice.toLocaleString('en-IN')})` : ''}
                                      </option>
                                    ))}
                                </optgroup>
                              )}

                            </select>
                            {line.missingFromMaster && !(line.rate > 0) && (
                              <p role="status" className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                Not in master data: enter the rate (and HSN/SAC and unit) yourself.
                              </p>
                            )}
                            <div className="relative pt-0.5">
                              <textarea
                                rows={2}
                                placeholder="Add note / item specifications..."
                                className="w-full text-[11px] text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 bg-slate-50/80 dark:bg-slate-800/60 rounded-md border border-slate-200/90 dark:border-slate-700/90 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 px-2.5 py-1 transition-colors resize-y"
                                value={line.notes ?? ''}
                                onChange={e => updateLine(line.key, { notes: e.target.value })}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    {/* HSN */}
                    {form.showHsnColumn && (
                      <td className="px-2 py-2">
                        <input
                          className={INPUT + ' text-xs text-center'}
                          placeholder="#"
                          value={line.hsnSac}
                          onChange={e => updateLine(line.key, { hsnSac: e.target.value })}
                        />
                      </td>
                    )}
                    {/* Qty */}
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="1"
                        className={INPUT + ' text-xs text-center'}
                        value={line.quantity === 0 ? '' : line.quantity}
                        onChange={e => updateLine(line.key, { quantity: e.target.value === '' ? 0 : Number(e.target.value) || 0 })}
                      />
                    </td>
                    {/* Unit */}
                    <td className="px-2 py-2 min-w-[90px]">
                      <select
                        className={INPUT + ' text-xs text-center px-1.5 py-2 cursor-pointer'}
                        value={unitOptions.find(u => u.toLowerCase() === (line.unit || 'nos').toLowerCase()) || line.unit || 'Nos'}
                        onChange={e => updateLine(line.key, { unit: e.target.value })}
                      >
                        {unitOptions.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                        {line.unit && !unitOptions.some(u => u.toLowerCase() === line.unit.toLowerCase()) && (
                          <option value={line.unit}>{line.unit}</option>
                        )}
                      </select>
                    </td>
                    {/* Rate */}
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className={INPUT + ' text-xs text-right'}
                        value={line.rate === 0 ? '' : line.rate}
                        onChange={e => updateLine(line.key, { rate: e.target.value === '' ? 0 : Number(e.target.value) || 0 })}
                      />
                    </td>
                    {/* Disc % */}
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="0"
                        className={INPUT + ' text-xs text-center w-full min-w-[56px]' + (baseRateViolation ? ' border-amber-400 dark:border-amber-500' : '')}
                        value={line.discount === 0 ? '' : line.discount}
                        onChange={e => updateLine(line.key, { discount: e.target.value === '' ? 0 : Number(e.target.value) || 0, discountType: 'percent' })}
                      />
                    </td>
                    {/* Taxable */}
                    <td className="px-2 py-2 text-right text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {formatINR(calc.amount)}
                    </td>
                    {/* Tax type — picked even when this quotation shows no GST, so the proforma inherits it */}
                    <td className="px-2 py-2">
                      <TaxSlabSelect
                        slabs={taxMasters}
                        value={line.taxMasterId ?? ''}
                        onChange={(taxMasterId, rate) => updateLine(line.key, rate === undefined
                          ? { taxMasterId }
                          : { taxMasterId, gstRate: rate, cgstRate: rate / 2, sgstRate: rate / 2, igstRate: 0 })}
                      />
                    </td>
                    {/* GST % */}
                    {form.gstEnabled && (
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          placeholder="18"
                          className={INPUT + ' text-xs text-center w-full min-w-[50px]'}
                          value={line.gstRate === 0 ? '' : line.gstRate}
                          onChange={e => updateLine(line.key, { gstRate: e.target.value === '' ? 0 : Number(e.target.value) || 0 })}
                        />
                      </td>
                    )}
                    {/* Total */}
                    <td className="px-2 py-2 text-right text-xs font-bold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                      {formatINR(calc.total)}
                    </td>
                    {/* Delete */}
                    <td className="px-2 py-2 text-center">
                      {form.lines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLine(line.key)}
                          className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete row"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {baseRateViolation && (
                    <tr key={`${line.key}-base-rate`} className="bg-amber-50/80 dark:bg-amber-950/30">
                      <td
                        colSpan={8 + (form.showHsnColumn ? 1 : 0) + (form.gstEnabled ? 1 : 0)}
                        className="px-3 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400"
                      >
                        ⚠ {baseRateWarningText(baseRateViolation)}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add line buttons */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button type="button" onClick={addLine}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add New Line
            </button>
            {products.length > 0 && (
              <select
                className="text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value=""
                onChange={e => {
                  if (e.target.value) {
                    const newLineKey = Math.random().toString(36).slice(2);
                    setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine(), key: newLineKey }] }));
                    pickProduct(newLineKey, e.target.value);
                  }
                }}
              >
                <option value="">+ Quick Add Item from Catalog</option>
                {products.map(p => <option key={p._id} value={p._id}>{p.name}{p.sellingPrice ? ` - ₹${p.sellingPrice}` : ''}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ── Totals Panel ───────────────────────────────────────────────────── */}
      <div className={SECTION_CARD + ' p-5'}>
        <div className="max-w-sm ml-auto space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Gross Amount</span>
            <span className="font-medium">{formatINR(totals.grossTotal)}</span>
          </div>
          {totals.totalItemDiscount > 0 && (
            <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
              <span>Item Discount</span>
              <span>-{formatINR(totals.totalItemDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-slate-700 dark:text-slate-200 font-semibold">Total Amount</span>
            <span className="font-semibold">{formatINR(totals.subtotal)}</span>
          </div>
          {totals.headerDisc > 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Additional Discount</span><span>-{formatINR(totals.headerDisc)}</span>
            </div>
          )}
          {form.gstEnabled && totals.taxTotal > 0 && (
            <div className="flex justify-between text-sm text-indigo-600 dark:text-indigo-400 font-medium">
              <span>GST ({form.reverseCharge ? 'Reverse Charge' : 'Tax'})</span>
              <span>+{formatINR(totals.taxTotal)}</span>
            </div>
          )}
          {form.additionalCharges > 0 && (
            <div className="flex justify-between text-sm text-slate-500">
              <span>{form.additionalChargesLabel}</span><span>{formatINR(form.additionalCharges)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold border-t border-slate-200 dark:border-slate-700 pt-2">
            <span>Grand Total ({form.currency})</span>
            <span className="text-indigo-700 dark:text-indigo-400">{formatINR(totals.grandTotal)}</span>
          </div>
          <p className="text-[10px] text-slate-400 italic text-right">{toWords(Math.round(totals.grandTotal))}</p>
        </div>
      </div>

      {/* ── Notes ─────────────────────────────────────────────────────────── */}
      <Section title="Notes">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Any additional notes to include in the quotation. Each line becomes a separate bullet point.</p>
        <textarea className={INPUT + ' resize-none'} rows={3} placeholder="Enter notes, one per line...&#10;e.g. Price valid for 30 days&#10;GST extra as applicable"
          value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
      </Section>

      {/* ── Payment Terms ──────────────────────────────────────────────────── */}
      <Section title="Payment Terms">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Choose the payment term from master data. It is printed on the quotation.</p>
        <select
          className={INPUT + ' text-xs py-2'}
          value={customPaymentTerm || (!selectedPaymentTerm && form.paymentTerms) ? PAYMENT_TERM_CUSTOM : (selectedPaymentTerm?._id || '')}
          onChange={e => {
            const v = e.target.value;
            if (v === PAYMENT_TERM_CUSTOM) {
              setCustomPaymentTerm(true);
              return;
            }
            setCustomPaymentTerm(false);
            const t = activePaymentTerms.find(x => x._id === v);
            setForm(f => ({ ...f, paymentTerms: t?.name || '' }));
          }}
        >
          <option value="">— Select a payment term —</option>
          {activePaymentTerms.map(t => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
          <option value={PAYMENT_TERM_CUSTOM}>Custom…</option>
        </select>
        {selectedPaymentTerm?.description && !customPaymentTerm && (
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">{selectedPaymentTerm.description}</p>
        )}
        {(customPaymentTerm || (!selectedPaymentTerm && form.paymentTerms)) && (
          <input
            className={INPUT + ' text-xs py-2 mt-2'}
            placeholder="e.g. 100% Full Payment on Delivery / Order"
            value={form.paymentTerms}
            onChange={e => setForm(f => ({ ...f, paymentTerms: e.target.value }))}
          />
        )}
      </Section>

      {/* ── Terms & Conditions ─────────────────────────────────────────────── */}
      <Section title="Terms & Conditions">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">Payment terms, delivery timeline, warranty, and legal conditions. Select from master data or write custom.</p>
          {defaultTermsArray.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setForm(f => ({ ...f, termsList: [...defaultTermsArray], terms: defaultTerms }));
                toast.success(`Loaded ${defaultTermsArray.length} default terms from master data`);
              }}
              className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline shrink-0 ml-2"
            >
              Reset to Master Data Defaults
            </button>
          )}
        </div>
        <select
          className={INPUT + ' text-xs mb-2'}
          value=""
          onChange={e => {
            const val = e.target.value;
            if (!val) return;
            if (val === 'custom') {
              setForm(f => ({ ...f, termsList: [...(f.termsList || []), ''] }));
            } else {
              const selected = termConditions.find(t => t.id === val);
              if (selected) {
                const content = (selected.content || '').trim();
                const termsArr = content ? content.split('\n').map(s => s.trim()).filter(Boolean) : [selected.title];
                setForm(f => {
                  const current = (f.termsList || []).filter(s => s && s.trim());
                  const updated = [...current, ...termsArr];
                  return { ...f, termsList: updated, terms: updated.join('\n') };
                });
                toast.success(`Added "${selected.title}" to terms`);
              }
            }
          }}
        >
          <option value="">— Add term from master data —</option>
          {(termConditions || []).filter(t => t.isActive !== false).map(t => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
          <option value="custom">+ Custom empty term</option>
        </select>
        <div className="space-y-2 mb-3">
          {(form.termsList || []).map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-5 shrink-0 text-right">{i + 1}.</span>
              <input
                className={INPUT + ' text-xs flex-1'}
                value={item}
                onChange={e => {
                  const newList = [...(form.termsList || [])];
                  newList[i] = e.target.value;
                  setForm(f => ({ ...f, termsList: newList, terms: newList.filter(s => s.trim()).join('\n') }));
                }}
                placeholder="Term / condition text..."
              />
              <button
                type="button"
                onClick={() => {
                  const newList = (form.termsList || []).filter((_, idx) => idx !== i);
                  setForm(f => ({ ...f, termsList: newList, terms: newList.filter(s => s.trim()).join('\n') }));
                }}
                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 shrink-0"
                title="Remove term"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setForm(f => ({ ...f, termsList: [...(f.termsList || []), ''] }));
          }}
          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add term
        </button>
      </Section>

      {/* ── Assumptions & Exclusions (hidden when disabled in master data) ──── */}
      {assumptionsSectionEnabled && (
      <Section title="Assumptions & Exclusions">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">Scope boundaries, client dependencies, and revision policies. Select from master data or add custom.</p>
          {defaultAssumptionsArray.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setForm(f => ({
                  ...f,
                  assumptionsList: [...defaultAssumptionsArray],
                  assumptions: defaultAssumptions + (defaultExclusions ? '\n' + defaultExclusions : ''),
                }));
                toast.success(`Loaded ${defaultAssumptionsArray.length} default assumptions & exclusions from master data`);
              }}
              className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline shrink-0 ml-2"
            >
              Reset to Master Data Defaults
            </button>
          )}
        </div>
        <select
          className={INPUT + ' text-xs mb-2'}
          value=""
          onChange={e => {
            const val = e.target.value;
            if (!val) return;
            if (val === 'custom') {
              setForm(f => ({ ...f, assumptionsList: [...(f.assumptionsList || []), ''] }));
            } else {
              const selected = assumptionsExclusions.find(a => a.id === val);
              if (selected) {
                const isExcl = String(selected.category || '').toUpperCase() === 'EXCLUSION';
                const prefix = isExcl ? 'EXCLUSION: ' : '';
                const assumptionText = prefix + selected.description;
                setForm(f => {
                  const current = (f.assumptionsList || []).filter(s => s && s.trim());
                  const updated = [...current, assumptionText];
                  return { ...f, assumptionsList: updated, assumptions: updated.join('\n') };
                });
                toast.success(`Added "${selected.title}"`);
              }
            }
          }}
        >
          <option value="">— Add assumption / exclusion from master data —</option>
          {(assumptionsExclusions || []).filter(a => a.isActive !== false).map(a => {
            const isExcl = String(a.category || '').toUpperCase() === 'EXCLUSION';
            return (
              <option key={a.id} value={a.id}>
                {(isExcl ? 'EXCLUSION: ' : 'ASSUMPTION: ')}{a.title ? `${a.title} — ` : ''}{a.description.length > 55 ? a.description.slice(0, 55) + '...' : a.description}
              </option>
            );
          })}
          <option value="custom">+ Custom empty item</option>
        </select>
        <div className="space-y-2 mb-3">
          {(form.assumptionsList || []).map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-5 shrink-0 text-right">{i + 1}.</span>
              <input
                className={INPUT + ' text-xs flex-1'}
                value={item}
                onChange={e => {
                  const newList = [...(form.assumptionsList || [])];
                  newList[i] = e.target.value;
                  setForm(f => ({ ...f, assumptionsList: newList, assumptions: newList.filter(s => s.trim()).join('\n') }));
                }}
                placeholder="Assumption or exclusion item..."
              />
              <button
                type="button"
                onClick={() => {
                  const newList = (form.assumptionsList || []).filter((_, idx) => idx !== i);
                  setForm(f => ({ ...f, assumptionsList: newList.length ? newList : [''], assumptions: newList.filter(s => s.trim()).join('\n') }));
                }}
                className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 shrink-0"
                title="Remove item"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => {
            setForm(f => ({ ...f, assumptionsList: [...(f.assumptionsList || []), ''] }));
          }}
          className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add assumption / exclusion
        </button>
      </Section>
      )}

      {/* ── Bank & Settlement Details ───────────────────────────────────────── */}
      <Section title="Bank & Settlement Details">
        <div className="mb-3"><LockedHint source="bank" /></div>
        <div className="flex items-center gap-2 mb-3">
          <label className={LABEL + ' mb-0'}>Select Bank Account</label>
          <select className={INPUT + ' w-full text-xs'} value={form.bankDetailId || ''} onChange={e => {
            const selected = bankDetails.find(b => b.id === e.target.value);
            if (selected) {
              setForm(f => ({
                ...f,
                bankDetailId: selected.id,
                bankAccName: selected.accountName,
                bankAccNo: selected.accountNumber,
                bankName: selected.bankName,
                bankBranch: selected.branch,
                bankIfsc: selected.ifsc,
                bankUpi: selected.upi || '',
              }));
            } else {
              setForm(f => ({ ...f, bankDetailId: '' }));
            }
          }}>
            <option value="">{form.bankAccNo ? '— As saved on this document —' : '— Select bank account —'}</option>
            {(bankDetails || []).filter(b => b.isActive).map(b => (
              <option key={b.id} value={b.id}>{b.accountName} — {b.bankName} ({b.accountNumber.slice(-4)})</option>
            ))}
          </select>
          <a href="/app/settings/master-data/bank-details" target="_blank" rel="noopener" className="text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap">
            <ExternalLink className="w-3.5 h-3.5 inline" /> Manage
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={LABEL}>Account Name</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankAccName} readOnly aria-readonly="true" />
          </div>
          <div>
            <label className={LABEL}>Account Number</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankAccNo} readOnly aria-readonly="true" />
          </div>
          <div>
            <label className={LABEL}>Bank Name</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankName} readOnly aria-readonly="true" />
          </div>
          <div>
            <label className={LABEL}>Branch</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankBranch} readOnly aria-readonly="true" />
          </div>
          <div>
            <label className={LABEL}>IFSC Code</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankIfsc} readOnly aria-readonly="true" />
          </div>
          <div>
            <label className={LABEL}>UPI ID</label>
            <input className={INPUT + ' text-xs ' + LOCKED_INPUT_CLASS} value={form.bankUpi} readOnly aria-readonly="true" />
          </div>
        </div>
      </Section>

      {/* ── Declaration ────────────────────────────────────────────────────── */}
      <Section title="Declaration">
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Legal certification rendered at the bottom of the quotation document.</p>
        <div className="mb-3">
          <label className={LABEL}>Select from Master Data</label>
          <select className={INPUT + ' text-xs'} value={form.declarationId || ''} onChange={e => {
            const selected = declarations.find(d => d.id === e.target.value);
            if (selected) {
              setForm(f => ({ ...f, declarationId: selected.id, declaration: selected.content }));
            } else {
              setForm(f => ({ ...f, declarationId: '', declaration: '' }));
            }
          }}>
            <option value="">— Custom / None —</option>
            {(declarations || []).filter(d => d.isActive).map(d => (
              <option key={d.id} value={d.id}>{d.title}</option>
            ))}
          </select>
        </div>
        <textarea className={INPUT + ' resize-none text-xs'} rows={3} placeholder="Declaration text..."
          value={form.declaration} onChange={e => setForm(f => ({ ...f, declaration: e.target.value }))} />
      </Section>

      {/* ── Advanced Options ───────────────────────────────────────────────── */}
      <Section title="Advanced Options">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { key: 'showHsnColumn', label: 'Show HSN/SAC column' },
            { key: 'showTaxSummary', label: 'Show tax summary in quotation' },
            { key: 'showSkuColumn', label: 'Show SKU in Quotation' },
            { key: 'showSerialNumbers', label: 'Show Serial Numbers in Quotation' },
            { key: 'showThumbnails', label: 'Show thumbnails in separate column' },
            { key: 'showDescFullWidth', label: 'Show description in full width' },
            { key: 'hideSubtotalForGroups', label: 'Hide subtotal for group items' },
            { key: 'hidePlaceOfSupply', label: 'Hide place/country of supply' },
          ].map(opt => (
            <div key={opt.key} className="flex items-center gap-3">
              <input type="checkbox" id={`opt-${opt.key}`}
                checked={(form as any)[opt.key]}
                onChange={e => setForm(f => ({ ...f, [opt.key]: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
              <label htmlFor={`opt-${opt.key}`} className="text-sm text-slate-700 dark:text-slate-300">{opt.label}</label>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <label className={LABEL + ' mb-0'}>Display unit as</label>
            <select className={INPUT + ' w-32'} value={form.displayUnit}
              onChange={e => setForm(f => ({ ...f, displayUnit: e.target.value as 'short' | 'full' }))}>
              <option value="short">Short (Nos)</option>
              <option value="full">Full (Numbers)</option>
            </select>
          </div>
        </div>
      </Section>

      {/* ── Action Buttons ─────────────────────────────────────────────────── */}
      {baseRateViolations.length > 0 && (
        <BaseRateWarningBanner violations={baseRateViolations} documentLabel="quotation" />
      )}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        {editInPlace ? (
          <Button onClick={() => handleSubmit(true)} disabled={saving || masterDataBlocked}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Save Changes
          </Button>
        ) : revisedFrom ? (
          <Button onClick={() => handleSubmit(false)} disabled={saving || masterDataBlocked || baseRateViolations.length > 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Create Revised Quotation
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={() => handleSubmit(true)} disabled={saving || masterDataBlocked}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Save as Draft
            </Button>
            <Button onClick={() => handleSubmit(false)} disabled={saving || masterDataBlocked || baseRateViolations.length > 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              {editId ? 'Confirm Quotation' : 'Create Quotation'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
